import {
  getIntersectionRoot,
  getScrollEventTarget,
  getScrollTop,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

const FOOTER_APPROACH_MARGIN = '0px 0px 200px 0px';

const STYLE_VARS = [
  '--skrey-glass-bg',
  '--skrey-glass-blur',
  '--skrey-glass-radius',
  '--skrey-product-hero-title',
  '--skrey-product-hero-title-mb',
  '--skrey-product-hero-atc-bg',
  '--skrey-product-hero-atc-bg-hover',
  '--skrey-product-hero-atc-text',
  '--skrey-product-hero-atc-size',
  '--skrey-product-hero-atc-size-mb',
  '--skrey-product-hero-atc-offset',
  '--skrey-product-hero-atc-offset-mb',
  '--skrey-product-hero-atc-radius',
];

const ATC_SELECTOR =
  '.skrey-product-hero__card .add-to-cart-button, .buy-buttons-block .add-to-cart-button, .buy-buttons-block button[name="add"]';

const FOOTER_SELECTOR = 'footer, [class*="footer-group"]';

/**
 * Product-hero add-to-bag card: overlay morph, sticky visibility, quantity sync.
 */
class SkreyProductHeroCard extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerApproachObserver = null;

  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {Set<Element>} */
  #visible = new Set();

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLElement | null} */
  #sticky = null;

  /** @type {boolean} */
  #footerInView = false;

  /** @type {boolean} */
  #footerApproaching = false;

  /** @type {boolean} */
  #scrollingDown = false;

  /** @type {number} */
  #lastScrollTop = 0;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #atcReady = false;

  /** @type {MutationObserver | null} */
  #fullObserver = null;

  connectedCallback() {
    const sticky = this.querySelector('.skrey-sticky-atc');
    if (sticky instanceof HTMLElement) {
      this.#sticky = sticky;
      if (sticky.parentElement !== document.body) {
        this.#copyVars(sticky);
        document.body.append(sticky);
      }
    }

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1', 10) || 1;
    this.#updateQuantity();

    scrollContainerMediaQuery.addEventListener('change', this.#observe, { signal });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      this.#syncStuck();
    });

    this.#observe();
    this.#observeKeyIngredientsFull();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#fullObserver?.disconnect();
    this.#fullObserver = null;
    this.#teardown();
    this.#sticky?.remove();
    this.#sticky = null;
  }

  /**
   * @param {HTMLElement} sticky
   */
  #copyVars(sticky) {
    const styles = getComputedStyle(this);
    for (const name of STYLE_VARS) {
      const value = styles.getPropertyValue(name).trim();
      if (value) sticky.style.setProperty(name, value);
    }
  }

  #teardown() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#footerObserver?.disconnect();
    this.#footerObserver = null;
    this.#footerApproachObserver?.disconnect();
    this.#footerApproachObserver = null;
    if (this.#scrollTarget) {
      this.#scrollTarget.removeEventListener('scroll', this.#onScroll);
      this.#scrollTarget = null;
    }
    this.#visible.clear();
    this.#atcReady = false;
  }

  #observe = () => {
    this.#teardown();
    this.#setStuck(false);

    const root = getIntersectionRoot();
    const sticky = this.#sticky;
    const targets = this.#targets();

    if (targets.length > 0) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          this.#atcReady = true;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.#visible.add(entry.target);
            } else {
              this.#visible.delete(entry.target);
            }
          }

          this.#syncStuck();
        },
        {
          root,
          threshold: 0,
        }
      );

      for (const target of targets) {
        this.#observer.observe(target);
      }
    } else {
      this.#atcReady = true;
    }

    const footer = document.querySelector(FOOTER_SELECTOR);

    if (sticky && footer) {
      this.#lastScrollTop = getScrollTop();
      this.#scrollTarget = getScrollEventTarget();
      this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });

      this.#footerObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;

          this.#footerInView = entry.isIntersecting;
          this.#syncStuck();
        },
        { root, threshold: 0 }
      );

      this.#footerApproachObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;

          this.#footerApproaching = entry.isIntersecting;
          this.#syncStuck();
        },
        {
          root,
          rootMargin: FOOTER_APPROACH_MARGIN,
        }
      );

      this.#footerObserver.observe(footer);
      this.#footerApproachObserver.observe(footer);
    } else {
      this.#footerInView = false;
      this.#footerApproaching = false;
    }

    this.#syncStuck();
  };

  #onScroll = () => {
    const top = getScrollTop();
    this.#scrollingDown = top > this.#lastScrollTop;
    this.#lastScrollTop = top;
    this.#syncStuck();
  };

  /**
   * @returns {Element[]}
   */
  #targets() {
    const nodes = document.querySelectorAll(ATC_SELECTOR);
    const sticky = this.#sticky;

    return [...nodes].filter((node) => !sticky?.contains(node));
  }

  #syncStuck() {
    this.#setStuck(this.#shouldShow());
  }

  #observeKeyIngredientsFull() {
    this.#fullObserver?.disconnect();
    this.#fullObserver = new MutationObserver(() => this.#syncStuck());
    this.#fullObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-skrey-key-ingredients-full'],
    });
  }

  #shouldShow() {
    if (!this.#sticky || !this.#atcReady) return false;
    if (this.#isChatActive()) return false;
    if (document.documentElement.hasAttribute('data-skrey-key-ingredients-full')) return false;
    if (this.#footerInView) return false;
    if (this.#scrollingDown && this.#footerApproaching) return false;
    return this.#visible.size === 0;
  }

  /**
   * Inbox upgrades `<shopify-chat>` only when the app is installed.
   * The placeholder in chat-drawer stays inert without it.
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * @param {boolean} stuck
   */
  #setStuck(stuck) {
    const sticky = this.#sticky;
    if (!sticky) return;

    if (stuck) sticky.inert = false;
    sticky.classList.toggle('is-stuck', stuck);
    sticky.dataset.stuck = stuck ? 'true' : 'false';
    sticky.setAttribute('aria-hidden', stuck ? 'false' : 'true');
    if (!stuck) sticky.inert = true;
  }

  /**
   * @param {Event} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('product-card') || event.target.closest('quick-add-dialog')) return;

    const productSelectEvent = /** @type {Event & { promise?: Promise<{ detail?: any }> }} */ (event);
    if (!productSelectEvent.promise) return;

    productSelectEvent.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, newProduct } = detail;

        if (newProduct?.id) {
          this.dataset.productId = String(newProduct.id);
        } else if (productId && this.dataset.productId && productId !== this.dataset.productId) {
          return;
        }

        const overlay = this.querySelector('.skrey-product-hero__card');
        const nextOverlay = html.querySelector('.skrey-product-hero__card');
        if (overlay && nextOverlay) morph(overlay, nextOverlay);

        const bar = this.#sticky?.querySelector('.skrey-sticky-atc__bar');
        const nextBar = html.querySelector('.skrey-sticky-atc__bar');
        if (bar && nextBar) morph(bar, nextBar, { childrenOnly: true });

        this.#updateQuantity();
        this.#observe();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[skrey-sticky-atc] Product select failed:', error);
      });
  };

  /**
   * @param {Event} event
   */
  #handleQuantityUpdate = (event) => {
    const detail = /** @type {{ quantity?: number, cartLine?: number }} */ (event).detail;
    if (detail?.cartLine) return;
    if (typeof detail?.quantity !== 'number') return;

    this.#currentQuantity = detail.quantity;
    this.#updateQuantity();
  };

  #updateQuantity() {
    const quantity = this.#currentQuantity;
    const inputs = [
      ...this.querySelectorAll('input[name="quantity"]'),
      ...(this.#sticky?.querySelectorAll('input[name="quantity"]') ?? []),
    ];

    for (const input of inputs) {
      if (input instanceof HTMLInputElement) input.value = String(quantity);
    }

    const qtyEls = this.#sticky?.querySelectorAll('.skrey-product-hero__atc-qty') ?? [];

    for (const el of qtyEls) {
      if (!(el instanceof HTMLElement)) continue;

      const number = el.querySelector('.skrey-product-hero__atc-qty-number');
      if (number) number.textContent = String(quantity);

      const button = el.closest('button');
      const available = button ? !button.disabled : true;
      el.hidden = !(available && quantity > 1);
    }
  }
}

if (!customElements.get('skrey-product-hero-card')) {
  customElements.define('skrey-product-hero-card', SkreyProductHeroCard);
}
